import { useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';

export const FUND_CLIENT_STATUSES = ['lead', 'preparing', 'submitted', 'approved', 'rejected', 'active', 'reporting', 'closed'];
export const blankFundClient = {
  fund_id: '', company_id: '', status: 'lead',
  grant_requested: '', grant_approved: '', fee_pct: '', our_fee: '',
  submitted_at: '', approved_at: '', notes: '',
};

/**
 * Enroll / edit a client on a fund. `lockFund` pins the fund (used from the
 * FundDetail page); `clients` is the list of client companies to choose from.
 */
export default function FundClientForm({ initial, clients = [], lockFund = false, isAdmin = false, onClose, onSaved, onDeleted }) {
  const confirm = useConfirm();
  const toast = useToast();
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...blankFundClient, ...initial });
  const [baseline] = useState(() => JSON.stringify({ ...blankFundClient, ...initial }));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    setBusy(true); setErrors({});
    try {
      if (isNew) await api.post('/fund-clients', form);
      else await api.put(`/fund-clients/${initial.id}`, form);
      toast(isNew ? t('Client enrolled') : t('Enrollment saved'));
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Remove enrollment'), message: t('Remove this client enrollment and everything under it (documents, milestones, tasks)?'), confirmLabel: t('Remove'), danger: true }))) return;
    await api.del(`/fund-clients/${initial.id}`);
    toast(t('Enrollment removed'));
    (onDeleted || onSaved)();
  }

  return (
    <Modal title={isNew ? t('Enroll client') : t('Edit enrollment')} onClose={onClose}
      dirty={JSON.stringify(form) !== baseline}
      footer={
        <div className="modal-actions">
          {!isNew && isAdmin && <button className="btn danger ghost" onClick={remove}>{t('Remove')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}

      <div className="grid-2">
        <Field label={t('Client')} error={errors.company_id}>
          <select value={form.company_id || ''} onChange={set('company_id')} disabled={!isNew}>
            <option value="">—</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label={t('Status')}><select value={form.status} onChange={set('status')}>{FUND_CLIENT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
      </div>

      <div className="grid-2">
        <Field label={t('Grant requested (€)')}><input type="number" step="0.01" value={form.grant_requested ?? ''} onChange={set('grant_requested')} /></Field>
        <Field label={t('Grant approved (€)')}><input type="number" step="0.01" value={form.grant_approved ?? ''} onChange={set('grant_approved')} /></Field>
      </div>
      <div className="grid-2">
        <Field label={t('Our fee %')}><input type="number" step="0.01" value={form.fee_pct ?? ''} onChange={set('fee_pct')} /></Field>
        <Field label={t('Our fee (€, override)')}><input type="number" step="0.01" value={form.our_fee ?? ''} onChange={set('our_fee')} /></Field>
      </div>
      <div className="grid-2">
        <Field label={t('Submitted on')} error={errors.submitted_at}><input type="date" value={form.submitted_at || ''} onChange={set('submitted_at')} /></Field>
        <Field label={t('Approved on')} error={errors.approved_at}><input type="date" value={form.approved_at || ''} onChange={set('approved_at')} /></Field>
      </div>
      <Field label={t('Notes')}><textarea rows={3} value={form.notes || ''} onChange={set('notes')} /></Field>
    </Modal>
  );
}
